import { useState, useMemo, useEffect } from 'react';
import PageTransition from '@/components/ui/PageTransition';
import { Card, Empty, Button, theme } from 'antd';
import { MessageOutlined, EyeInvisibleOutlined, EyeOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { PageHeader } from '@/components/ui/PageHeader';
import { createFileRoute } from '@tanstack/react-router';
import { useGetChatThreadsQuery } from '@/api/client-communications';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';

import {
  ThreadsStats,
  ThreadsFilters,
  ThreadsTable,
  threadKeyOf,
  useThreadBulkActions,
  type FlowOption,
  type ThreadVisibility
} from '@/components/threads';
import { BulkActionBar, DeleteEntityModal } from '@/components/shared';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { toFlowOption } from '@/components/threads/utils';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { useThreadListFilters } from '@/hooks/threads/useThreadListFilters';

dayjs.extend(relativeTime);

export const Route = createFileRoute('/manage/threads/')({
  component: ThreadsCommunicationsComponent
});

function ThreadsCommunicationsComponent() {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  
  // Main list vs. hidden threads, and the bulk selection within the current view
  const [visibility, setVisibility] = useState<ThreadVisibility>('active');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const bulkActions = useThreadBulkActions({
    onHiddenChanged: () => setSelectedKeys([]),
    // Keep only the rows that failed selected so the admin can retry them.
    onDeleted: setSelectedKeys
  });
  const { requestDelete } = bulkActions;

  // Query for chat threads with auto-refresh support
  const {
    data: chatThreadsData,
    isLoading,
    isError,
    refetch,
    isFetching,
    dataUpdatedAt
  } = useGetChatThreadsQuery(
    undefined,
    true,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );

  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  // Search, date and column filters for the current view
  const {
    filters,
    tableFilters,
    setTableFilters,
    updateFilter,
    resetFilters,
    filteredThreads,
    hasActiveFilters
  } = useThreadListFilters(chatThreadsData?.threads, chatThreadsData?.flows, { visibility });

  const hiddenCount = useMemo(
    () => (chatThreadsData?.threads ?? []).filter((thread) => thread.hidden).length,
    [chatThreadsData?.threads]
  );

  // A new view or filter starts with an empty selection.
  useEffect(() => {
    setSelectedKeys([]);
  }, [visibility, filters, tableFilters]);

  // Auto-refresh can remove rows (hidden elsewhere, deleted); keep only keys still listed.
  useEffect(() => {
    const listed = new Set(filteredThreads.map(threadKeyOf));
    setSelectedKeys((previous) => {
      const next = previous.filter((key) => listed.has(key));
      return next.length === previous.length ? previous : next;
    });
  }, [filteredThreads]);

  // Flow options for select (use flows array with flowName if available)
  const flowOptions: FlowOption[] = useMemo(() => {
    return (chatThreadsData?.flows || []).map((flow) => toFlowOption(flow.flowId, flow.flowName));
  }, [chatThreadsData?.flows]);

  // Calculate stats
  const totalThreads = chatThreadsData?.threads?.length || 0;
  const totalFlows = chatThreadsData?.flows?.length || 0;
  const recentThreads = chatThreadsData?.threads?.filter(
    (thread) => dayjs(thread.updatedAt).isAfter(dayjs().subtract(24, 'hours'))
  ).length || 0;

  const header = (
    <PageHeader
      icon={<MessageOutlined />}
      title={t('threads.title')}
      description={t('threads.description')}
      actions={
        <AutoRefreshToggle
          autoRefresh={autoRefresh}
          onAutoRefreshChange={setAutoRefresh}
          isFetching={isFetching}
          lastUpdated={lastUpdatedText}
        />
      }
    />
  );

  if (isError) {
    return (
      <PageTransition animation="fadeInUp">
        {header}
        <Card>
          <Empty description={t('threads.loadError')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => refetch()}>
              {t('threads.tryAgain')}
            </Button>
          </Empty>
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition animation="fadeInUp">
      {header}

      <ThreadsStats
        stats={{
          totalThreads,
          filteredCount: filteredThreads.length,
          totalFlows,
          recentThreads,
          hasActiveFilters,
          isLoading
        }}
      />

      <ThreadsFilters
        filters={filters}
        filteredCount={filteredThreads.length}
        totalCount={totalThreads}
        hasActiveFilters={hasActiveFilters}
        isFetching={isFetching}
        onFilterChange={updateFilter}
        onRefresh={refetch}
        onReset={resetFilters}
        visibility={visibility}
        hiddenCount={hiddenCount}
        onVisibilityChange={setVisibility}
      />

      <BulkActionBar selectedCount={selectedKeys.length} onClearSelection={() => setSelectedKeys([])}>
        <Button
          icon={visibility === 'hidden' ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          loading={bulkActions.pendingKeys.length > 0}
          onClick={() => bulkActions.toggleHidden(visibility, selectedKeys)}
        >
          {visibility === 'hidden'
            ? t('threads.table.showAgainSelected', { count: selectedKeys.length })
            : t('threads.table.hideSelected', { count: selectedKeys.length })}
        </Button>
        {requestDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={() => requestDelete(selectedKeys)}>
            {t('threads.table.deleteSelected', { count: selectedKeys.length })}
          </Button>
        )}
      </BulkActionBar>

      <ThreadsTable
        threads={filteredThreads}
        totalThreads={totalThreads}
        flowOptions={flowOptions}
        tableFilters={tableFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
        onTableFilterChange={setTableFilters}
        onResetFilters={resetFilters}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        visibility={visibility}
        onToggleHidden={(threadKeys) => bulkActions.toggleHidden(visibility, threadKeys)}
        onDelete={requestDelete}
        pendingKeys={bulkActions.pendingKeys}
      />

      <DeleteEntityModal {...bulkActions.deleteModalProps} />
    </PageTransition>
  );
}
